/**
 * Settings › HR (piece 15, Settings › HR; piece 34): the five effective-dated
 * day counts with the owner's defaults of 2026-09-30, and the external
 * escalation route (decision 0137). The keys live in the Settings Index's own
 * module beside `hr.documentRetentionYears`; the defaults and the refusals live
 * here, beside the module that reads them.
 */
import {
  HR_ABSENCE_ESCALATION_TIMEOUT_DAYS_KEY, HR_ACKNOWLEDGEMENT_REMINDER_DAYS_KEY, HR_AWAITING_DELIVERY_STALL_DAYS_KEY, HR_CERTIFICATION_EXPIRY_LEAD_DAYS_KEY,
  HR_DOCUMENT_RETENTION_YEARS_KEY, HR_EXTERNAL_ESCALATION_ROUTE_KEY, HR_PUNCH_RADIUS_METERS_KEY, HR_RETALIATION_GUARD_DAYS_KEY,
  HR_SUBSTANCE_CONSEQUENCES_KEY, HR_SUBSTANCE_PROCEDURE_KEY, HR_SUBSTANCE_TESTING_RULE_KEY,
} from '../settings/settingsIndex';
import { HR_PUNCH_RADIUS_DEFAULT_METRES } from './punchRadius';

/** The certification register's lead time: a renewal To-Do is raised this many days before a completion lapses. */
export const HR_CERTIFICATION_EXPIRY_LEAD_DAYS_DEFAULT = 60;

/** 🔒 The five day counts in the order the owner gave them: 60, 90, 5, 3 and 3 (2026-09-30). */
export const HR_DAY_COUNT_DEFAULTS: readonly { key: string; value: number }[] = [
  { key: HR_CERTIFICATION_EXPIRY_LEAD_DAYS_KEY, value: HR_CERTIFICATION_EXPIRY_LEAD_DAYS_DEFAULT },
  { key: HR_RETALIATION_GUARD_DAYS_KEY, value: 90 },
  { key: HR_ACKNOWLEDGEMENT_REMINDER_DAYS_KEY, value: 5 },
  { key: HR_AWAITING_DELIVERY_STALL_DAYS_KEY, value: 3 },
  { key: HR_ABSENCE_ESCALATION_TIMEOUT_DAYS_KEY, value: 3 },
];

/**
 * The rows the seed writes once at Organization scope: the five counts, and
 * the external route seeded blank. 🔨 A blank route is honest and a wrong one
 * is not (piece 15): naming counsel nobody has retained promises a route that
 * does not answer.
 */
export const PIECE_34_SETTINGS: readonly { key: string; value: number | string }[] = [
  ...HR_DAY_COUNT_DEFAULTS,
  { key: HR_EXTERNAL_ESCALATION_ROUTE_KEY, value: '' },
  // Decision 12931: the radius a gate punch may stand from the site's pin, seeded 500 meters.
  { key: HR_PUNCH_RADIUS_METERS_KEY, value: HR_PUNCH_RADIUS_DEFAULT_METRES },
];

/**
 * 🔒 The keys Settings › HR › HR Rules edits (decision 3182): the five counts, the external route and the
 * retention years. A holder of `hr.hire` reads and writes these and no other Organization setting.
 */
export const HR_RULES_KEYS: readonly string[] = [...PIECE_34_SETTINGS.map((row) => row.key), HR_DOCUMENT_RETENTION_YEARS_KEY, HR_SUBSTANCE_PROCEDURE_KEY, HR_SUBSTANCE_CONSEQUENCES_KEY, HR_SUBSTANCE_TESTING_RULE_KEY];

/**
 * 🔒 Decision 10652: the substance policy's three keys, each a list of lines an administrator fills on HR Rules ›
 * Substance Policy and none seeded. The procedure gates the drug test record: none is written while it holds no line.
 */
export const HR_SUBSTANCE_POLICY_KEYS: readonly string[] = [HR_SUBSTANCE_PROCEDURE_KEY, HR_SUBSTANCE_CONSEQUENCES_KEY, HR_SUBSTANCE_TESTING_RULE_KEY];

/** The most lines one substance key holds, and the longest line: a procedure step or a table row fits well inside both. */
export const HR_SUBSTANCE_LINES_MAXIMUM = 50;
export const HR_SUBSTANCE_LINE_MAXIMUM = 500;

/** One row of the consequence table: the offense before the first colon, the consequence after it. */
export interface SubstanceConsequenceRow {
  offense: string;
  consequence: string;
}

/** 🔒 The consequence table read from its lines; a line without both sides of a colon reads as no row. */
export function substanceConsequenceRows(lines: readonly string[]): SubstanceConsequenceRow[] {
  return lines.flatMap((line) => {
    const at = line.indexOf(':');
    if (at < 0) return [];
    const offense = line.slice(0, at).trim();
    const consequence = line.slice(at + 1).trim();
    return offense && consequence ? [{ offense, consequence }] : [];
  });
}

/**
 * 🔨 A substance key is a list of lines, at most fifty of at most 500 characters, and a blank clears it. A
 * consequence line names the offense, a colon and the consequence, and a line missing either side is refused.
 */
export function hrSubstancePolicyProblem(key: string, value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  if (!Array.isArray(value) || value.some((line) => typeof line !== 'string')) return 'A substance policy entry is a list of lines of text.';
  const lines = value as string[];
  if (lines.length > HR_SUBSTANCE_LINES_MAXIMUM) return `A substance policy entry holds at most ${HR_SUBSTANCE_LINES_MAXIMUM} lines.`;
  if (lines.some((line) => line.length > HR_SUBSTANCE_LINE_MAXIMUM)) return `Each line of a substance policy entry is at most ${HR_SUBSTANCE_LINE_MAXIMUM} characters.`;
  if (key === HR_SUBSTANCE_CONSEQUENCES_KEY) {
    const bad = lines.find((line) => substanceConsequenceRows([line]).length === 0);
    if (bad !== undefined) return `Each consequence line names the offense, a colon, then the consequence; "${bad}" does not.`;
  }
  return null;
}


/** The longest a day count may run: ten years of days, which no HR window approaches. */
export const HR_DAY_COUNT_MAXIMUM = 3650;

/** A day count is a whole number of days from 1 up; a blank is refused, because each row is read as a window. */
export function hrDayCountProblem(value: unknown): string | null {
  const days = typeof value === 'number' ? value : Number(value);
  if (value === null || value === undefined || value === '' || !Number.isInteger(days) || days < 1 || days > HR_DAY_COUNT_MAXIMUM) {
    return `An HR day count is a whole number of days from 1 to ${HR_DAY_COUNT_MAXIMUM}.`;
  }
  return null;
}

/** The longest external route the screen keeps: a name, a firm, a telephone number and an address fit well inside it. */
export const HR_EXTERNAL_ROUTE_MAXIMUM = 500;

/** The route is text and may be blank; past the limit it is refused. */
export function hrExternalRouteProblem(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value !== 'string') return 'The external escalation route is a line of text.';
  if (value.length > HR_EXTERNAL_ROUTE_MAXIMUM) return `The external escalation route is at most ${HR_EXTERNAL_ROUTE_MAXIMUM} characters.`;
  return null;
}
